/**
 * The game-facing audio layer: four events in, notes and haptics out.
 *
 * The design rule for every sound here is that the player should be able to
 * close their eyes and still know what the board is doing. So:
 *   - forward moves climb the scale, one degree per tile,
 *   - backtracking plays the note it climbed from, quieter and darker, so
 *     undo is audible as undo rather than as another step,
 *   - the melody wraps to the root every eleven tiles, giving the drag a
 *     phrase structure instead of an undifferentiated rise,
 *   - filling the board opens the filter, so the board audibly brightens as
 *     it fills,
 *   - the win resolves to the root.
 */

import { AudioEngine } from "./engine.ts";
import { cinematicPack } from "./packs/cinematic.ts";
import type { SoundPack } from "./pack.ts";
import { indexForPathLength, scaleFrequencies, WIN_ARPEGGIO, WIN_CHORD } from "./scale.ts";
import { tapBackward, tapForward, tapReset, tapWin } from "./haptics.ts";

/** Notes closer together than this get softened — see `crowding`. */
const CROWD_WINDOW_MS = 90;

/**
 * Spaced to the pack's swell, not to a pluck: notes that bloom over ~0.3s
 * played 85ms apart smear into one chord instead of reading as a rising
 * figure. Slower steps let each one arrive before the next begins, which is
 * also simply what the cinematic register sounds like.
 */
const ARPEGGIO_STEP_SECONDS = 0.24;
/** Let the final step of the path land before the win figure starts. */
const WIN_LEAD_SECONDS = 0.14;

export class GameAudio {
  private readonly engine: AudioEngine;
  private lastNoteAt = Number.NEGATIVE_INFINITY;

  constructor(pack: SoundPack = cinematicPack) {
    this.engine = new AudioEngine(pack, scaleFrequencies());
  }

  /** Wire this to pointerdown — see AudioEngine.unlock. */
  unlock(): void {
    this.engine.unlock();
  }

  get muted(): boolean {
    return this.engine.isMuted;
  }

  /** Returns the new state, so the caller can label its button from it. */
  toggleMuted(): boolean {
    const next = !this.engine.isMuted;
    this.engine.setMuted(next);
    return next;
  }

  /** The path grew to `length` tiles out of `total` playable. */
  advance(length: number, total: number): void {
    const progress = total > 0 ? Math.min(1, length / total) : 0;
    this.engine.play(indexForPathLength(length), {
      velocity: 0.78 * this.crowding(),
      brightness: 0.45 + 0.5 * progress,
    });
    tapForward();
  }

  /** The path shrank back to `length` tiles. */
  retreat(length: number, total: number): void {
    if (length < 1) return;
    const progress = total > 0 ? Math.min(1, length / total) : 0;
    this.engine.play(indexForPathLength(length), {
      velocity: 0.44 * this.crowding(),
      brightness: 0.12 + 0.2 * progress,
      // Retreats do not get to ring — a long tail on an undone move would
      // pile up under the notes that replace it.
      damp: 0.32,
    });
    tapBackward();
  }

  /** The board was cleared. */
  reset(): void {
    this.engine.play(0, { velocity: 0.34, brightness: 0.1, damp: 0.22 });
    tapReset();
  }

  /** Solved: rising figure, then a chord on the root. */
  win(): void {
    WIN_ARPEGGIO.forEach((index, i) => {
      this.engine.play(index, {
        velocity: 0.5 + 0.05 * i,
        brightness: 0.8 + 0.02 * i,
        when: WIN_LEAD_SECONDS + i * ARPEGGIO_STEP_SECONDS,
      });
    });

    const chordAt = WIN_LEAD_SECONDS + WIN_ARPEGGIO.length * ARPEGGIO_STEP_SECONDS + 0.12;
    for (const index of WIN_CHORD) {
      this.engine.play(index, { velocity: 0.42, brightness: 0.95, when: chordAt });
    }

    // A long bloom, timed to outlast the on-screen reveal rather than the
    // arpeggio — the tail is still ringing while the photo fades up.
    this.engine.swellReverb(2.2, 5);
    tapWin();
  }

  /**
   * A fast drag crosses tiles far quicker than a harp is played, and at full
   * velocity that reads as a machine gun. Softening notes that land inside
   * the window turns a quick sweep into a shimmer instead.
   */
  private crowding(): number {
    const now = performance.now();
    const gap = now - this.lastNoteAt;
    this.lastNoteAt = now;
    return Math.min(1, Math.max(0.45, gap / CROWD_WINDOW_MS));
  }
}

export type { SoundPack } from "./pack.ts";
